import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
} from "framer-motion";
import GlassScene from "./GlassScene";

const EASE = [0.16, 1, 0.3, 1];
const TITLE = "FINDLEY ST";

function TitleLetters() {
  return (
    <span className="inline-flex flex-wrap justify-center" aria-label={TITLE}>
      {TITLE.split("").map((ch, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="inline-block"
          initial={{ opacity: 0, y: 60, rotateX: -50 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ delay: 1.15 + i * 0.045, duration: 0.9, ease: EASE }}
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const progressRef = useRef(0);
  const reduced = useReducedMotion() ?? false;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    progressRef.current = v;
  });

  const titleOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const titleScale = useTransform(scrollYProgress, [0, 0.12], [1, 0.93]);
  const titleY = useTransform(scrollYProgress, [0, 0.12], [0, -40]);

  const w1Opacity = useTransform(scrollYProgress, [0.12, 0.18, 0.26, 0.33], [0, 1, 1, 0]);
  const w1Y = useTransform(scrollYProgress, [0.12, 0.18], [50, 0]);
  const w2Opacity = useTransform(scrollYProgress, [0.35, 0.42, 0.48, 0.54], [0, 1, 1, 0]);
  const w2Y = useTransform(scrollYProgress, [0.35, 0.42], [50, 0]);
  const w3Opacity = useTransform(scrollYProgress, [0.56, 0.62, 0.72, 0.78], [0, 1, 1, 0]);
  const w3Y = useTransform(scrollYProgress, [0.56, 0.62], [50, 0]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.02, 0.1], [1, 1, 0]);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative h-[260vh]">
      <div className="sticky top-0 z-10 h-screen overflow-hidden">
        <GlassScene progressRef={progressRef} reduced={reduced} />

        {/* pristine state */}
        <motion.div
          style={reduced ? undefined : { opacity: titleOpacity, scale: titleScale, y: titleY }}
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1, ease: EASE }}
            className="hero-kicker mb-6 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs"
            data-text="Web design & development studio"
          >
            Web design &amp; development studio
          </motion.p>
          <h1 className="text-center font-display text-[13vw] font-semibold leading-[0.9] tracking-[-0.04em] md:text-[9vw]">
            <TitleLetters />
          </h1>
        </motion.div>

        {/* fracture words */}
        {!reduced && (
          <>
            <motion.div
              style={{ opacity: w1Opacity, y: w1Y }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <p className="font-display text-[11vw] font-medium tracking-[-0.03em] text-outline-strong md:text-[7vw]">
                BREAK APART
              </p>
            </motion.div>
            <motion.div
              style={{ opacity: w2Opacity, y: w2Y }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <p className="font-display text-[11vw] font-medium tracking-[-0.03em] text-outline-strong md:text-[7vw]">
                RETHINK
              </p>
            </motion.div>
            <motion.div
              style={{ opacity: w3Opacity, y: w3Y }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <p className="font-display text-[11vw] font-semibold tracking-[-0.03em] md:text-[7vw]">
                REBUILD
              </p>
            </motion.div>
          </>
        )}

        {/* the next scene itself rises behind the clearing glass — no stand-in
            text here; the manifesto headline arrives through the pane */}

        {/* scroll cue */}
        {!reduced && (
          <motion.div
            style={{ opacity: cueOpacity }}
            data-testid="hero-scroll-cue"
            className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] text-fog">Scroll</span>
            <span className="block h-12 w-px overflow-hidden bg-white/15">
              <span
                className="block h-full w-full bg-white"
                style={{ animation: "scroll-cue 1.8s cubic-bezier(0.16,1,0.3,1) infinite" }}
              />
            </span>
          </motion.div>
        )}
      </div>
    </section>
  );
}
